import { Component, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { getAuth, User } from 'firebase/auth';
import { arrayUnion, doc, getDoc, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from 'src/app/app.component';
import { Role } from 'src/app/enums';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-new-organisation',
  templateUrl: './new-organisation.component.html',
  styleUrls: ['./new-organisation.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatDialogActions,
    MatButton,
    MatDialogClose,
  ],
})
export class NewOrganisationComponent implements OnInit {
  user: User;
  newOrgForm: FormGroup;
  creating = signal(false);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialogRef: MatDialogRef<NewOrganisationComponent>,
  ) {}

  ngOnInit() {
    this.user = getAuth().currentUser;
    this.newOrgForm = this.fb.group({
      organisationName: ['', [Validators.required]],
    });
  }

  async create() {
    if (!this.newOrgForm.valid || this.creating()) {
      return this.newOrgForm.markAllAsTouched();
    }
    this.creating.set(true);
    const form = this.newOrgForm.value;
    console.log('form', form);
    const newOrgId = (form.organisationName as string).replaceAll(/\W/g, '').toLowerCase();
    console.log('newOrgId', newOrgId);
    const newOrgDoc = doc(db, `organisations/${newOrgId}`);
    const newOrgExists = (await getDoc(newOrgDoc)).exists();
    console.log('newOrgExists', newOrgExists);
    if (newOrgExists) {
      return this.snackBar.open('De gekozen organisatienaam bestaat al.', 'X', {
        duration: 5000,
      });
    }
    const generalSettingsDoc = doc(db, `organisations/${newOrgId}/settings/general`);
    const publicSettingsDoc = doc(db, `organisations/${newOrgId}/settings/public`);
    const rootUserDoc = doc(db, `users/${this.user.uid}`);
    const newOrgUserDoc = doc(db, `organisations/${newOrgId}/users/${this.user.uid}`);
    await setDoc(newOrgDoc, {
      name: form.organisationName,
      domain: 'hallobuurtvervoer',
    });

    //upload default images from assets
    const images = {} as any;
    const filePath = `organisations/${newOrgId}/organisation_images/${newOrgId}-`;
    const imageUploadData = [
      { name: 'favicon', fileName: 'default-favicon.ico' },
      { name: 'loginImage', fileName: 'default-login-image.svg' },
      { name: 'banner', fileName: 'default-banner.png' },
      { name: 'logo', fileName: 'default-logo.png' },
    ];
    for (const imageData of imageUploadData) {
      const response = await fetch(`assets/default-images/${imageData.fileName}`);
      const blob = await response.blob();
      const file = new File([blob], imageData.fileName, { type: blob.type });
      if (file) {
        await uploadBytes(ref(storage, filePath + imageData.fileName), file);
        images[imageData.name] = await getDownloadURL(ref(storage, filePath + imageData.fileName));
      }
    }

    await setDoc(generalSettingsDoc, {
      name: form.organisationName,
      privacyLink: 'https://hallobuurtvervoer.nl/privacy-verklaring/',
      primaryColor: '#484b99',
      accentColor: '#42af5a',
      signalColor: '#FFC0CB',
      pinAddCredits: '0000',
      pinSubtractCredits: '0000',
      maxActiveVehicles: 1,
      ...images,
    });

    await setDoc(publicSettingsDoc, {
      name: form.organisationName,
      privacyLink: 'https://hallobuurtvervoer.nl/privacy-verklaring/',
      primaryColor: '#484b99',
      accentColor: '#42af5a',
      ...images,
    });
    await setDoc(
      rootUserDoc,
      {
        organisations: arrayUnion({
          roles: [Role.Administrator],
          id: newOrgId,
          name: form.organisationName,
        }),
      },
      { merge: true },
    );
    await setDoc(newOrgUserDoc, {
      email: this.user.email,
      roles: [Role.Administrator],
      superuser: true,
    });
    this.router.navigate([`/set/${newOrgId}`]);
    this.dialogRef.close();
  }
}
